/*
Turn one email's HTML into a block document — or decline to.

    htmlToBlocks(html) -> { ok, reason, doc, unconverted, changes, stats }

Pure: no React, no DOM, no clock, no network, no Mongo. CommonJS, so it runs
under plain `node src/utils/emailHtmlToBlocks.test.js`.

WHAT THIS IS FOR, AND WHAT IT IS NOT FOR
----------------------------------------
The conversion probe (.superpowers/sdd/2026-09-02-block-conversion-probe.md)
measured all 88 stored templates and recommended NOT building a general
converter, for a reason that still stands: 86 of 86 PS templates have images
outside every editable cell, most templates carry a responsive scaffold no block
document can hold, and a conversion that quietly drops a masthead is worse than
no conversion at all.

One family came out clean at the time: the 26 MJML `ps-pro-{access-,}networking-
area-*` transactional emails. Three rounds of widening later all 78 active PS
templates convert, and every one of them losslessly — which is not the same
thing as the probe having been wrong. Each widening replaced a refusal that
described the CONVERTER's limits with a reading backed by something written in
the document: a CSS rule (./emailStyleSheet), the shape of a cell, an element
foster-parented out of a table, a closing tag with no opener. Nothing was
loosened towards a guess, and the cases the probe was right to refuse still
refuse — a background image, an image with no measurable width anywhere, a
picture of ambiguous size between two icons, prose beside a table in a <div>,
crossed nesting. There are tests for each of those, against real bytes.

It is not tuned per template; it is a set of rules, and a template that does not
fit them comes back with `ok: false` and a list of what it could not place. The
refusal is the feature. A converter that silently drops a paragraph would be
indistinguishable, on screen, from one that worked.

WHY THERE IS NO SECOND HTML SCANNER IN HERE
-------------------------------------------
The reading of the document — where the cells are, what counts as visible text,
which markup is inside an MSO comment — comes from `./emailCellFinder`, which is
`ps-back/util/email/editableCells.js` itself, not a copy of it. See that file's
header for why the cross-package require is the right seam. The probe's own
HTML->block rules used to live in `blockConversionProbe.js`; they live HERE now,
and the probe requires them from here, so there is one answer to "what is a
button" rather than two that can drift.

The node reader below (`topLevelNodes` and friends) is only ever pointed at a
cell's inner HTML, which the finder has already proved balanced. That is what
makes plain depth counting safe: there is no implied-close markup to recover
from, because an unbalanced fragment never reaches it.

WHAT IS REGENERATED RATHER THAN PRESERVED
-----------------------------------------
Everything structural. `renderBlocks` builds its own document from scratch, so
the MSO conditionals, the ghost tables, the exact table nesting and the per-cell
padding of the original are NOT carried across — they are replaced by the
serialiser's own, which is a different set of the same kind of thing. That is not
a bug to be fixed here; it is the deal, and `changes` says so in words the
reviewer reads before accepting.
*/

const {
  cellSpans, findEditableCells, cellCoverage, visibleText, scanOutsideComments, isTagBalanced,
} = require('./emailCellFinder');
const { declarations, readStyleSheet, resolveRules } = require('./emailStyleSheet');
const { BLOCK_DEFAULTS, DEFAULT_SETTINGS, clamp } = require('./emailBlocks');

// --- a very small top-level HTML reader ------------------------------------

const VOID_ELEMENTS = [
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta',
  'param', 'source', 'track', 'wbr',
];

// Same attribute-run trick as CELL_TAG in editableCells.js: skip over quoted
// values so a `>` inside an attribute cannot end the tag early.
const ANY_TAG = '<(/?)([a-zA-Z][a-zA-Z0-9-]*)((?:[^>"\']|"[^"]*"|\'[^\']*\')*?)(/?)>';

const textNode = (raw, outerStart) => ({
  kind: 'text', raw, outerStart, outerEnd: outerStart + raw.length,
});

/**
 * The immediate children of a fragment, as { kind, tag, attrs, inner, raw }.
 * Text between elements is returned too, because "a single <img> and nothing
 * else" and "a single <img> plus a caption" are different classifications.
 */
function topLevelNodes(html) {
  const nodes = [];
  let depth = 0;
  let cursor = 0;
  let open = null;

  scanOutsideComments(html, ANY_TAG).forEach((match) => {
    const isClose = match[1] === '/';
    const tag = match[2].toLowerCase();
    const isEmpty = match[4] === '/' || VOID_ELEMENTS.indexOf(tag) !== -1;

    if (!isClose && isEmpty) {
      if (depth === 0) {
        nodes.push(textNode(html.slice(cursor, match.index), cursor));
        nodes.push({
          kind: 'element',
          tag,
          attrs: match[3],
          inner: '',
          raw: match[0],
          // Offsets INTO `html`, so a caller that knows where `html` itself
          // starts can turn a node back into a document offset. `innerStart` is
          // what `fosterRuns` recurses with.
          outerStart: match.index,
          outerEnd: match.index + match[0].length,
          innerStart: match.index + match[0].length,
        });
        cursor = match.index + match[0].length;
      }
      return;
    }

    if (!isClose) {
      if (depth === 0) {
        nodes.push(textNode(html.slice(cursor, match.index), cursor));
        open = {
          tag, attrs: match[3], start: match.index, innerStart: match.index + match[0].length,
        };
      }
      depth += 1;
      return;
    }

    if (depth === 0) return; // stray close; cells are balanced, so this is dead defence
    depth -= 1;
    if (depth === 0 && open) {
      nodes.push({
        kind: 'element',
        tag: open.tag,
        attrs: open.attrs,
        inner: html.slice(open.innerStart, match.index),
        raw: html.slice(open.start, match.index + match[0].length),
        outerStart: open.start,
        outerEnd: match.index + match[0].length,
        innerStart: open.innerStart,
      });
      cursor = match.index + match[0].length;
      open = null;
    }
  });

  nodes.push(textNode(html.slice(cursor), cursor));
  return nodes;
}

// Whitespace-only and entity-only text between elements is formatting, not
// content; an element is always significant.
const significant = nodes => nodes.filter(node => (
  node.kind === 'element' || visibleText(node.raw) !== ''
));

const attrOf = (node, name) => {
  const match = new RegExp(`${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'i').exec(node.attrs || '');
  if (!match) return '';
  return match[2] === undefined ? match[3] : match[2];
};

const styleOf = node => attrOf(node, 'style');

const tagsIn = html => scanOutsideComments(html, ANY_TAG)
  .filter(match => match[1] !== '/')
  .map(match => match[2].toLowerCase());

const countTag = (html, tag) => tagsIn(html).filter(name => name === tag).length;

const unique = list => list.filter((item, index) => list.indexOf(item) === index);

// --- reading an element's style --------------------------------------------
//
// `declarations` — a style attribute as a { property: value } map — now lives in
// ./emailStyleSheet, because the rule bodies in a <style> block are read exactly
// the same way and two readings of `padding:14px 52px` would be one too many. It
// is still re-exported from here, where the probe already imports it from.

const classesOf = node => String(attrOf(node, 'class') || '').split(/\s+/).filter(Boolean);

/**
 * Everything that styles one element: the document's own base rules first, then
 * its `style` attribute on top.
 *
 * The attribute wins, which is the real cascade — an inline declaration outranks
 * any rule in a stylesheet — and it is also the conservative order here: every
 * measurement this converter made before the stylesheet was read is unchanged,
 * and the sheet can only fill in a property that was not written on the tag.
 */
function resolved(node, sheet) {
  const fromSheet = resolveRules(sheet, node.tag, classesOf(node));
  const inline = declarations(styleOf(node));
  return { ...fromSheet, ...inline };
}

// A raw opening tag — `<td class="innerpadding" style="…">` — as something
// `resolved` can read. The cell tags this module carries around are strings
// rather than nodes, and their class is as much a measurement as their style.
const TAG_SHAPE = /^<([a-zA-Z][a-zA-Z0-9-]*)((?:[^>"']|"[^"]*"|'[^']*')*)/;

function nodeFromTag(tagHtml) {
  const match = TAG_SHAPE.exec(String(tagHtml || ''));
  if (!match) return { tag: '', attrs: '' };
  return { tag: match[1].toLowerCase(), attrs: match[2] };
}

// The first pixel measurement in a value, rounded. `null` when there is none —
// never 0, which is a legitimate measurement and would read as "found".
function px(value) {
  const match = /(-?\d+(?:\.\d+)?)\s*px/.exec(String(value || ''));
  return match ? Math.round(Number(match[1])) : null;
}

// Every pixel measurement in a value, in order. For shorthands: `padding:10px
// 25px` is (top/bottom, left/right).
function allPx(value) {
  const found = String(value || '').match(/-?\d+(?:\.\d+)?\s*px/g) || [];
  return found.map(entry => Math.round(Number(entry.replace(/px/, ''))));
}

// The horizontal half of a `padding` shorthand. One measurement means all four
// sides; two mean vertical then horizontal; none means the block's own default.
function sidePadding(measurements) {
  if (measurements.length > 1) return measurements[1];
  if (measurements.length === 1) return measurements[0];
  return BLOCK_DEFAULTS.button.paddingX;
}

const BORDER_KEYWORDS = ['solid', 'dashed', 'dotted', 'double', 'groove', 'ridge', 'inset', 'outset', 'none', 'hidden'];

// The colour out of a border shorthand — `solid 4px black` and `4px solid black`
// both give `black`. Whatever is left once the width and the line style are
// taken out.
function borderColour(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  const rest = parts.filter(part => !/px|%|em$/.test(part) && BORDER_KEYWORDS.indexOf(part.toLowerCase()) === -1);
  return rest.length ? rest.join(' ') : '';
}

// A colour out of `background` or `background-color`, given an element's
// resolved declarations. A `background` shorthand in this corpus is always just
// a colour; anything with a url() in it is refused earlier as a background
// image. One definition, because the card, the social chip, the divider and the
// button all ask the same question of the same kind of map.
function fillColour(decl) {
  const value = (decl || {})['background-color'] || (decl || {}).background || '';
  return /url\s*\(/i.test(value) ? '' : String(value).trim();
}

/**
 * Does this style DRAW something of its own — a fill, or an edge with width — as
 * opposed to only positioning and typography?
 *
 * The question a wrapper has to answer before it may be peeled off a picture. A
 * `<div align="center" style="line-height:10px">` draws nothing and can go; a
 * `<div style="background:#3b5998;border-radius:3px">` IS the coloured chip
 * under a white glyph, and peeling it leaves an invisible icon on a white card —
 * a loss that survives review because there is nothing left on screen to notice.
 */
function paints(decl) {
  const fill = decl['background-color'] || decl.background || '';
  if (String(fill).trim() !== '') return true;
  return ['border', 'border-top', 'border-right', 'border-bottom', 'border-left']
    .some((key) => {
      const value = decl[key];
      if (!value) return false;
      if (/\b(none|hidden)\b/i.test(value)) return false;
      const width = px(value);
      return width === null || width > 0;
    });
}

// --- what each block type can hold -----------------------------------------

// Tags `renderText` can carry through verbatim and still look like the prose it
// replaced. `div` is deliberately ABSENT: a div in this corpus usually carries
// layout (a width, a background, a text-align that positions a column), and
// calling those prose is exactly the kind of tuning that makes an answer look
// better than it is. A cell rejected only for a div is reported as such, so the
// cost of the strictness is visible rather than hidden.
const TEXT_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'span', 'a',
  'ul', 'ol', 'li', 'small', 'sub', 'sup', 'font', 'big',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];

// Those of TEXT_TAGS that are blocks. Everything else a text block may hold is
// inline, and inline is what `paragraphWrapLoose` puts a paragraph around.
// `li` is here so that a stray top-level one is left alone rather than sealed
// inside a <p>, which would be a worse document than the one it came from.
const TEXT_BLOCK_TAGS = ['p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

// What loose copy gets wrapped in. `margin:0` is not decoration: a bare <p> in
// an email client carries a 1em margin top and bottom that this copy never had,
// so wrapping without it would move every line of it down the page.
const LOOSE_OPEN = '<p style="margin:0">';
const LOOSE_CLOSE = '</p>';

/**
 * The same copy, with a paragraph around each run of it that has no block.
 *
 * WHY THIS EXISTS. A ProseMirror document is a list of blocks. Hand the rich
 * text editor a bare run of words — `Hola {{recipient}},` — and it wraps them in
 * a <p> the email never had, which is a GAINED element and as much a corruption
 * as a lost one. So the guard (tiptapConfig.js) refuses such a block outright,
 * and until this function existed that refusal covered 105 of the corpus's 204
 * text blocks: more than half the copy, editable only through the HTML escape
 * hatch.
 *
 * The answer is not to relax the guard. It is to make the document right at
 * rest: if the copy needs a paragraph in order to be a document, the paragraph
 * belongs in the stored block, put there once by the conversion — where it can
 * be measured against the original by the converter's own losslessness gate —
 * rather than invented by an editor every time somebody clicks into it.
 *
 * WHAT IT DOES NOT DO. A run that already sits in a block is untouched, so a
 * document that was already a list of blocks comes back byte for byte. Blocks
 * are not moved, merged or reordered: each MAXIMAL run of inline material
 * between two blocks gets one paragraph, and the whitespace at either end of
 * that run stays outside it, exactly where the document had it.
 */
function paragraphWrapLoose(html) {
  const nodes = topLevelNodes(html);
  const isBlock = node => node.kind === 'element' && TEXT_BLOCK_TAGS.indexOf(node.tag) !== -1;
  // An element is always something; text is something only when it has words in
  // it. Whitespace is formatting and does not, by itself, need a paragraph.
  const isContent = node => (node.kind === 'element' ? !isBlock(node) : visibleText(node.raw) !== '');

  const runs = [];
  let first = null;
  let last = null;
  const flush = () => {
    if (first !== null) runs.push({ start: first.outerStart, end: last.outerEnd });
    first = null;
    last = null;
  };

  nodes.forEach((node) => {
    if (isBlock(node)) {
      flush();
      return;
    }
    if (!isContent(node)) return; // blank text: neither starts a run nor ends one
    if (first === null) first = node;
    last = node;
  });
  flush();

  if (runs.length === 0) return html;

  let out = '';
  let cursor = 0;
  runs.forEach((run) => {
    // A run's first and last node can be text that BEGINS or ENDS in whitespace —
    // the newline and indentation the template is written with. That is the
    // document's formatting and it stays where the document put it, outside the
    // paragraph. Only a text node can put whitespace here: an element ends in '>'.
    let { start, end } = run;
    while (start < end && /\s/.test(html[start])) start += 1;
    while (end > start && /\s/.test(html[end - 1])) end -= 1;

    out += html.slice(cursor, start);
    out += LOOSE_OPEN + html.slice(start, end) + LOOSE_CLOSE;
    cursor = end;
  });
  return out + html.slice(cursor);
}

// Inline emphasis a `heading` block's plain-text `text` prop can survive losing.
// `a` is not here: `renderHeading` escapes its text, so a link inside a heading
// would be destroyed, not merely restyled.
const HEADING_INLINE = ['span', 'strong', 'b', 'em', 'i', 'u', 'small', 'font'];

// Wrappers that carry no content of their own. Unwrapping one is not free — its
// font, size, colour and alignment go with it — so what it was carrying is
// lifted onto the block's own typography props instead of being dropped.
const WRAPPER_TAGS = ['div', 'center', 'font'];

// Every comment in a fragment, verbatim. Used to decide whether a cell holds
// something a human wrote (refuse) or Outlook's own second rendering (read on).
const commentsIn = html => (String(html).match(/<!--[\s\S]*?-->/g) || []);

// An Outlook conditional: the `<!--[if …]>` half of the pair, its `<![endif]-->`
// half, and the downlevel-revealed `<!--<![endif]-->` some generators emit.
const MSO_COMMENT = /^<!--\s*(\[if\b|<!\[endif\])/i;

const mapped = (type, props, notes) => ({
  type, props, notes: notes || [], reason: '',
});
const unmapped = (reason, notes) => ({
  type: 'unmapped', props: null, notes: notes || [], reason,
});

/**
 * The one wrapper element that holds everything in this fragment, or null when
 * there is no such element — which is most of the time.
 *
 * THE ONE PEELER. Every caller that needs to see through a wrapper goes through
 * this: `unwrap` for a copy cell, `readImage` for a picture cell. It is not a new
 * HTML reader — it is three lines over `topLevelNodes`, the module's only
 * scanner, which is itself comment-aware through `scanOutsideComments`. Three
 * comment-blindness bugs in this repo came from a second scanner; there is not
 * going to be a fourth.
 *
 * WHY NOT `emailCellChrome.unwrapChrome`, which peels the typography wrapper off
 * a cell for the rich text editor. It refuses to peel a wrapper whose body is not
 * a document — `<div><a><img></a></div>` is inline, so it peels nothing — and
 * that refusal is load-bearing there: it is what stops the editor being handed a
 * bare run of words it would re-wrap in a <p> of its own invention, and what
 * keeps `rewrapChrome(unwrapChrome(x)) === x` useful. Here nothing is written
 * back, so the rule that protects a round trip only blocks a reading. The two
 * peelers answer different questions about the same bytes; neither is a copy of
 * the other's scanner.
 */
function soleWrapper(html) {
  const nodes = significant(topLevelNodes(html));
  if (nodes.length !== 1 || nodes[0].kind !== 'element') return null;
  if (WRAPPER_TAGS.indexOf(nodes[0].tag) === -1) return null;
  return nodes[0];
}

/**
 * Peel off wrappers that hold nothing but one other element, keeping what each
 * one was styling.
 *
 * Returns { html, style } — the innermost content, and the merged declarations
 * of every wrapper peeled, outermost first so an inner wrapper wins. That merged
 * style is what becomes the block's font, size, line height and colour: the
 * probe recorded these as losses, and carrying them is the whole point of the
 * typography props added alongside this module.
 */
function unwrap(html, notes, sheet) {
  let current = html;
  let style = {};
  for (let depth = 0; depth < 8; depth += 1) {
    const wrapper = soleWrapper(current);
    if (wrapper === null) return { html: current, style };
    style = { ...style, ...resolved(wrapper, sheet) };
    notes.push(`wrapper <${wrapper.tag}> unwrapped; its type is carried on the block`);
    current = wrapper.inner;
  }
  return { html: current, style };
}

// The alignment a style's `text-align` asks for, or '' when it asks for nothing.
const alignFrom = (style, fallback) => {
  const value = String((style || {})['text-align'] || '').trim().toLowerCase();
  return ['left', 'center', 'right'].indexOf(value) === -1 ? fallback : value;
};

/**
 * The alignment every top-level element of a cell's copy asks for, or '' when
 * they do not all ask for the same one.
 *
 * A block has ONE alignment, so this is the only way a paragraph's own
 * `text-align` can survive into the block model rather than being contradicted
 * by a wrapper that said nothing. Loose text between the elements, or any
 * element without an explicit alignment, means no unanimous answer: something
 * in there is inheriting, and adopting an alignment would move it.
 */
function unanimousAlign(elements, hasLooseText, sheet) {
  if (hasLooseText || !elements || elements.length === 0) return '';
  const first = alignFrom(resolved(elements[0], sheet), '');
  if (!first) return '';
  return elements.every(element => alignFrom(resolved(element, sheet), '') === first) ? first : '';
}

/**
 * The typography props a `text` or `heading` block should carry, read off the
 * wrapper style that is about to be thrown away.
 *
 * A missing declaration becomes '' — which the serialiser reads as "the
 * document's" — rather than a guessed number. Empty is a real answer here.
 */
function typographyFrom(style) {
  const decl = style || {};
  const size = px(decl['font-size']);
  const height = px(decl['line-height']);
  return {
    fontSize: size === null ? '' : size,
    lineHeight: height === null ? '' : height,
    color: (decl.color || '').trim(),
    fontFamily: (decl['font-family'] || '').trim(),
  };
}

/**
 * classifyCell(innerHtml, sheet) -> { type, props, reason, notes }
 *
 * `type` is a block type or 'unmapped'. `props` is what to build the block from.
 * `notes` records what the block model will NOT carry even where the cell DOES
 * map — the raw material for the "and this will change" list the reviewer reads.
 *
 * `sheet` is the document's base style rules (./emailStyleSheet), and is
 * optional: with none, every element is measured by its `style` attribute alone,
 * which is what this did before the oldest templates turned out to keep their
 * measurements in a <style> block. The probe calls it with one argument.
 *
 * This is the single definition of the inverse mapping. The probe imports it.
 */
function classifyCell(innerHtml, sheet) {
  const notes = [];
  const comments = commentsIn(innerHtml);
  const msoOnly = comments.every(comment => MSO_COMMENT.test(comment));
  // A comment that is not an Outlook conditional is something a human wrote and
  // this cannot read. Refuse before anything else, as before.
  if (comments.length > 0 && !msoOnly) {
    return unmapped('the cell contains an HTML comment', notes);
  }

  const peeled = unwrap(innerHtml, notes, sheet);
  const { html } = peeled;
  const type = typographyFrom(peeled.style);
  const nodes = significant(topLevelNodes(html));
  const elements = nodes.filter(node => node.kind === 'element');
  const hasLooseText = nodes.some(node => node.kind === 'text');
  const text = visibleText(html);
  const images = countTag(html, 'img');

  if (images > 0) {
    // Images are placed by the document walk below, from the cells the copy
    // finder withholds. One mixed in with prose belongs to neither and is the
    // honest refusal.
    return unmapped(`an image sits inside a paragraph (${images} <img> among the copy)`, notes);
  }

  // BUTTON — the corpus's own tell: an anchor styled display:inline-block.
  if (elements.length === 1 && !hasLooseText && elements[0].tag === 'a') {
    const anchor = elements[0];
    const style = resolved(anchor, sheet);
    // `display:inline-block` is the modern generators' tell. The hand-built
    // family writes `.button { display:block; width:130px; background:black;
    // border-radius:50px; padding:14px 52px }` instead — a block-level anchor
    // that PAINTS ITS OWN CHIP, which is a button by any reading of the markup
    // and of the screen. A bare `display:block` anchor that paints nothing is
    // still a link in a paragraph and still goes to `text`, where its markup is
    // carried forward verbatim.
    const chip = /inline-block/i.test(style.display || '')
      || (/^block$/i.test(String(style.display || '').trim()) && paints(style));
    if (chip) {
      const inner = unique(tagsIn(anchor.inner))
        .filter(tag => HEADING_INLINE.indexOf(tag) === -1 && tag !== 'br');
      if (inner.length > 0) {
        return unmapped(`the button wraps block markup <${inner.join('>, <')}>`, notes);
      }
      const href = attrOf(anchor, 'href');
      if (!href) return unmapped('the button anchor has no href', notes);
      // A conditional INSIDE the anchor would be part of the label, and the
      // label is read with visibleText, which drops comments. That is a silent
      // loss, so it is refused rather than read.
      if (/<!--/.test(anchor.inner)) {
        return unmapped('the button label contains an HTML comment', notes);
      }
      if (comments.length > 0) {
        notes.push(`the Outlook fallback beside this button (${comments.length} conditional comment${comments.length === 1 ? '' : 's'}, the <v:roundrect> VML rectangle) is dropped — the block serialiser writes its own Outlook rendering of the same button`);
      }

      const padding = allPx(style.padding || '');
      const radius = px(style['border-radius']);
      const size = px(style['font-size']);
      if (size !== null && size !== 16) {
        notes.push(`the button's ${size}px label is sent at 16px — the button block has no size prop`);
      }
      if (style['text-transform'] && style['text-transform'] !== 'none') {
        notes.push(`the button's text-transform:${style['text-transform']} is dropped`);
      }
      const fixedWidth = px(style.width);
      if (fixedWidth !== null) {
        notes.push(`the button's fixed ${fixedWidth}px width is dropped — a button block is as wide as its label plus its padding`);
      }
      if ((style['font-family'] || '').trim()) {
        notes.push(`the button's own ${style['font-family'].trim()} is dropped — a button block is set in the document's font`);
      }
      return mapped('button', {
        label: visibleText(anchor.inner),
        href,
        background: fillColour(style) || BLOCK_DEFAULTS.button.background,
        color: (style.color || '').trim() || BLOCK_DEFAULTS.button.color,
        radius: radius === null ? BLOCK_DEFAULTS.button.radius : radius,
        align: alignFrom(peeled.style, 'center'),
        paddingY: padding.length ? padding[0] : BLOCK_DEFAULTS.button.paddingY,
        // `padding:10px 25px` is (vertical, horizontal); `padding:8px` is both.
        paddingX: sidePadding(padding),
      }, notes);
    }
  }

  // Past the button, an Outlook conditional is still a refusal. `text` carries a
  // cell's markup FORWARD verbatim, so a conditional in one would be
  // transplanted into a document whose other branch no longer matches it, and
  // nothing in the preview would ever show that. The button is the exception
  // because it is the one block whose Outlook rendering `renderButton` writes
  // from scratch, so there is no second branch left to desynchronise.
  if (comments.length > 0) return unmapped('the cell contains an HTML comment', notes);

  // HEADING — a lone <h1>-<h6> holding nothing but emphasised text.
  if (elements.length === 1 && !hasLooseText && /^h[1-6]$/.test(elements[0].tag)) {
    const heading = elements[0];
    const withoutTrailingBreaks = heading.inner.replace(/(\s|<br\s*\/?>)+$/i, '');
    const bad = unique(tagsIn(withoutTrailingBreaks)).filter(tag => HEADING_INLINE.indexOf(tag) === -1);
    if (bad.length === 0) {
      const style = resolved(heading, sheet);
      const level = Number(heading.tag.slice(1));
      if (level > 3) notes.push(`<${heading.tag}> is sent as an h3 — the heading block stops at 3`);
      if (/<strong|<b\b/i.test(heading.inner)) notes.push('a heading is always bold in the block model; its inner <strong>/<b> is dropped');
      if (style['letter-spacing'] || style['text-transform']) {
        notes.push('the heading\'s letter-spacing / text-transform is dropped');
      }
      const size = px(style['font-size']);
      const height = px(style['line-height']);
      return mapped('heading', {
        text: visibleText(heading.inner),
        level: clamp(level, 1, 3, 2),
        align: alignFrom(style, alignFrom(peeled.style, 'left')),
        color: (style.color || type.color || '').trim() || BLOCK_DEFAULTS.heading.color,
        fontSize: size === null ? (type.fontSize || BLOCK_DEFAULTS.heading.fontSize) : size,
        lineHeight: height === null ? (type.lineHeight || '') : height,
        fontFamily: (style['font-family'] || type.fontFamily || '').trim(),
      }, notes);
    }
    // Falls through on purpose: an <h2> holding a link is still prose a text
    // block can carry verbatim, and carrying it beats refusing it.
  }

  // TEXT — anything else, provided every tag in it is prose.
  const bad = unique(tagsIn(html)).filter(tag => TEXT_TAGS.indexOf(tag) === -1);
  if (bad.length === 0) {
    if (text === '') return unmapped('the cell has no copy in it', notes);
    if ((peeled.style['mso-line-height-alt'] || '')) {
      notes.push('mso-line-height-alt goes with the wrapper; Outlook line spacing will change slightly');
    }
    return mapped('text', {
      // Loose copy gets a paragraph of its own here, at conversion time, so the
      // stored document is a list of blocks — which is what the rich text
      // editor requires and what stops it inventing one. See
      // `paragraphWrapLoose`.
      html: paragraphWrapLoose(html.trim()),
      // The wrapper's alignment when it has one; otherwise the copy's OWN, when
      // every paragraph in it agrees. Reading only the wrapper used to leave a
      // centred paragraph inside a block whose `align` said 'left' — the panel
      // then showed the wrong answer, and now that a set property strips the
      // declaration it duplicates (emailTypographyOverride.js) it would also
      // shove 64 centred legal-copy paragraphs across this corpus to the left.
      // Unanimity is required: with one paragraph centred and one not, adopting
      // 'center' would move the one that never asked for it, so the block stays
      // left and each paragraph keeps what it had.
      align: alignFrom(peeled.style, unanimousAlign(elements, hasLooseText, sheet) || 'left'),
      fontSize: type.fontSize,
      lineHeight: type.lineHeight,
      color: type.color,
      fontFamily: type.fontFamily,
    }, notes);
  }
  if (bad.length === 1 && bad[0] === 'div') {
    return unmapped('a layout <div> sits beside the copy inside this cell', notes);
  }
  return unmapped(`the copy contains <${bad.join('>, <')}>, which no block type carries`, notes);
}

// --- reading the document ---------------------------------------------------

// A leaf cell is one that nests no other cell and no table: the smallest box the
// document actually puts something in. Everything above it is layout, and layout
// is what `renderBlocks` regenerates. Same rule findEditableCells uses to decide
// a cell is copy rather than structure, applied one step earlier so image-only
// and rule-only cells are seen too.
//
// COMMENT-AWARE, and it has to be. `findEditableCells` tests this with a plain
// regex and then refuses any cell holding an MSO conditional outright, which is
// right for an editor — editing one branch of a two-branch rendering and not the
// other desynchronises them silently. It is wrong here. MJML writes a divider as
// a modern `<p style="border-top">` FOLLOWED BY an Outlook-only ghost `<table>`
// in a conditional, inside the same cell; with the plain test that cell looks
// like layout, so the 4px black rule under the copy in all 26 templates was
// dropped without a word. A comment-aware test sees the `<p>`, and the ghost
// twin is a second rendering of the same rule which the serialiser regenerates
// in its own way.
const isLeaf = inner => scanOutsideComments(inner, '<(table|td|th)\\b').length === 0;


/**
 * The same fragment with any closing tag that never had an opener taken out, or
 * `null` when that is not what is wrong with it.
 *
 * THE ONE REPAIR THIS MODULE MAKES, AND THE ONLY ONE IT WILL MAKE. The Catalan
 * copy of `ps-presalesbcn26-ca` is stored with a `</strong>` that closes nothing:
 *
 *   …confirmar el teu registre per a la Fan Sale de <strong>Primavera Sound
 *   Barcelona 2026.</strong><br><br>
 *   Recorda que … has de confirmar abans el teu correu electrònic aquí:</strong><br>
 *
 * Its English, Spanish and Portuguese siblings do not have it. Every browser and
 * every email client discards that tag and draws the words around it, so the
 * email reads correctly today; `isTagBalanced` refuses the fragment, and the
 * whole template with it, for one character nobody can see.
 *
 * WHAT THIS IS NOT. It is not an HTML repairer. A close tag whose name IS
 * somewhere in the open stack — crossed nesting, `<b><i></b></i>` — returns
 * null, because there is no single reading of what the author meant. An opener
 * left unclosed returns null: the fragment then ends inside an element, which is
 * a cut through the middle of something and exactly what `isTagBalanced` exists
 * to catch. Only a close with no opener at all is dropped, only that tag is
 * removed, and every word around it is kept byte for byte. The result is checked
 * balanced before it is believed.
 */
function dropStrayCloses(fragment) {
  const stack = [];
  const strays = [];
  let crossed = false;

  scanOutsideComments(fragment, ANY_TAG).forEach((match) => {
    if (crossed) return;
    const isClose = match[1] === '/';
    const tag = match[2].toLowerCase();
    if (!isClose) {
      if (match[4] !== '/' && VOID_ELEMENTS.indexOf(tag) === -1) stack.push(tag);
      return;
    }
    if (stack.length > 0 && stack[stack.length - 1] === tag) {
      stack.pop();
      return;
    }
    if (stack.indexOf(tag) !== -1) { crossed = true; return; }
    strays.push({ tag, start: match.index, end: match.index + match[0].length });
  });

  if (crossed || stack.length > 0 || strays.length === 0) return null;

  let repaired = '';
  let cursor = 0;
  strays.forEach((stray) => {
    repaired += fragment.slice(cursor, stray.start);
    cursor = stray.end;
  });
  repaired += fragment.slice(cursor);

  if (!isTagBalanced(repaired)) return null;
  return { html: repaired, dropped: unique(strays.map(stray => stray.tag)) };
}

// The opening tag of the cell whose inner content starts at `start`. Used for
// the `align` attribute and the cell's own padding, neither of which is inside
// the span. A slice that does not begin with a cell tag is reported as no tag at
// all rather than guessed at — an attribute value holding a `<` is the case that
// would otherwise land here.
function openingTag(html, start) {
  const at = html.lastIndexOf('<', start - 1);
  if (at === -1) return '';
  const tag = html.slice(at, start);
  return /^<(td|th)\b/i.test(tag) ? tag : '';
}

const IMAGE_MIN_WIDTH = 200; // at or above this, a picture in its own right
const ICON_MAX_WIDTH = 60; // at or below this, one of a row of icons

/**
 * One picture, out of the node that holds it: its src, alt, measured width and
 * the href of the link around it, if any. `null` when the node is not an image
 * or a link around one.
 *
 * The width is read from the `width` attribute first, the element's own style
 * second — which since the stylesheet reader includes a class rule such as
 * `.logo { width: 60px }` — and the enclosing cell's `width:NNNpx` third. A
 * `width:100%` is not a measurement: it is a proportion of a box this converter
 * is about to replace, so it is reported as `fills: true` and no width, and the
 * caller decides whether it has a container width honest enough to stand in.
 * Past all of that the image has no size and is refused rather than guessed at,
 * because guessing puts a 550px masthead in the footer at 20px.
 */
function readOneImage(candidate, cellStyle, sheet) {
  let href = '';
  let node = candidate;
  if (node.tag === 'a') {
    href = attrOf(node, 'href');
    const insideLink = significant(topLevelNodes(node.inner));
    if (insideLink.length !== 1 || insideLink[0].kind !== 'element') return null;
    [node] = insideLink;
  }
  if (node.tag !== 'img') return null;

  const style = resolved(node, sheet);
  const width = px(attrOf(node, 'width'))
    || Number(attrOf(node, 'width')) || null;
  const styleWidth = px(style.width);
  const cellWidth = px(cellStyle.width);

  const measured = (Number.isFinite(width) && width > 0 ? width : null)
    || styleWidth
    || cellWidth;

  return {
    src: attrOf(node, 'src'),
    alt: attrOf(node, 'alt'),
    href,
    width: measured,
    // `img { width:100% }` or `.fullWidth { width:100% }`: the element says, in
    // the document's own words, that it fills whatever holds it.
    fills: measured === null && /(^|\s)100%$/.test(String(style.width || '').trim()),
  };
}

/**
 * Every picture in a cell that holds nothing but pictures, in document order.
 * `null` when the cell holds anything else at all.
 *
 * ONE image is a picture. SEVERAL are a row — the document put five linked
 * `<img>`s in one `<td align="center">`, which is a row of icons in the same way
 * that five `<td>`s in a `<tr>` would be, and it is the shape every one of the
 * oldest templates writes its social footer in:
 *
 *   <td class="logos-container" align="center">
 *     <a href="https://twitter.com/…"><img class="logo" src="…/twitter.png"></a>
 *     … five or six of them …
 *   </td>
 *
 * That cell used to be refused as "an image sits inside a paragraph", which was
 * the wrong reading of it: there is no paragraph, and nothing is mixed in.
 */
function readImages(inner, cellTag, sheet) {
  // Braze wraps every picture in `<div class="alignment" align="center">` and
  // then in an `<a>`. That is a wrapper shape, not a layout: the div positions
  // one image and holds nothing else, and the <a> is the picture's link, which
  // the image block has a prop for. Peel both.
  //
  // A wrapper that PAINTS is a different thing and is not peeled — a background
  // colour or a visible border on it is design the image block cannot carry, and
  // dropping it silently is how a coloured chip becomes a white square. Such a
  // cell comes back null and is refused with the rest.
  let content = inner;
  for (let depth = 0; depth < 8; depth += 1) {
    const wrapper = soleWrapper(content);
    if (wrapper === null) break;
    if (paints(resolved(wrapper, sheet))) return null;
    content = wrapper.inner;
  }

  const nodes = significant(topLevelNodes(content));
  if (nodes.length === 0) return null;
  if (nodes.some(node => node.kind === 'text')) return null;

  const cellStyle = resolved(nodeFromTag(cellTag), sheet);
  const images = nodes.map(node => readOneImage(node, cellStyle, sheet));
  return images.some(image => image === null) ? null : images;
}

/**
 * The coloured chip behind a social icon: the background and corner radius of
 * the table that immediately encloses the icon's own cell.
 *
 * WHY THIS IS WORTH THE TROUBLE. The icons in this corpus are white glyphs on
 * transparent PNGs, sitting on a `background:#3b5998;border-radius:3px` table.
 * Convert without the chip and four white shapes land on a white card: the
 * footer does not look different, it looks EMPTY, and it is the sort of loss
 * that survives a review because there is nothing there to notice.
 *
 * The rule is exact rather than approximate: the nearest `<table>` opened before
 * this cell, with no `</table>` between the two — which is precisely "the table
 * this cell is the first content of". A sibling table that already closed fails
 * that test and is not read, so a cell in the second column of a row cannot pick
 * up the styling of whatever the first column contained.
 */
const TABLE_TAG = '<table\\b((?:[^>"\']|"[^"]*"|\'[^\']*\')*?)>';
const EITHER_TABLE_TAG = '<(/?)table\\b((?:[^>"\']|"[^"]*"|\'[^\']*\')*?)>';

/**
 * The width a `width:100%` element actually fills: the innermost enclosing
 * `<table>` that states a width in pixels.
 *
 * `100%` is a proportion, and a proportion of a known number is a number. The
 * hand-built family's hero is `<img class="image-header">` under `img {
 * width:100% }` with no `.image-header` rule at all, inside
 * `<table class="content">` over `.content { max-width:900px }` — so 900px is a
 * derivation from two of the document's own measurements, not a guess about a
 * picture nobody measured.
 *
 * It is the INNERMOST such table on purpose. An image filling one 260px column
 * of a two-column row fills 260px, not the card, and taking the card's width
 * there would draw it three times too wide.
 *
 * The ancestors are the tables opened before this point and not yet closed —
 * one comment-aware scan through the module's only scanner, so a `<table>`
 * written inside an MSO conditional is not counted as an ancestor of anything.
 *
 * `null` when not one of them states a width in pixels. There is then nothing to
 * take a proportion OF, and the image is refused exactly as it was before this
 * function existed. The card's own width is deliberately not used as a
 * consolation: the card is not necessarily what a nested image fills.
 */
function containerWidth(html, at, sheet) {
  if (at <= 0) return null;
  const stack = [];
  scanOutsideComments(html.slice(0, at), EITHER_TABLE_TAG).forEach((match) => {
    if (match[1] === '/') stack.pop();
    else stack.push({ tag: 'table', attrs: match[2] });
  });
  for (let depth = stack.length - 1; depth >= 0; depth -= 1) {
    const decl = resolved(stack[depth], sheet);
    const maxWidth = px(decl['max-width']);
    const width = maxWidth === null ? px(decl.width) : maxWidth;
    if (width !== null && width > 0) return width;
  }
  return null;
}

function chipAround(html, cellTagStart, sheet) {
  if (cellTagStart <= 0) return { background: '', radius: 0 };
  const opens = scanOutsideComments(html.slice(0, cellTagStart), TABLE_TAG);
  if (opens.length === 0) return { background: '', radius: 0 };
  const last = opens[opens.length - 1];
  const between = html.slice(last.index + last[0].length, cellTagStart);
  if (scanOutsideComments(between, '</table\\b').length > 0) return { background: '', radius: 0 };
  const decl = resolved({ tag: 'table', attrs: last[1] }, sheet);
  const radius = px(decl['border-radius']);
  return { background: fillColour(decl), radius: radius === null ? 0 : radius };
}

/**
 * A cell that draws a rule and nothing else — an <hr>, or the empty <p> with a
 * border-top that MJML emits for a divider. Returns { thickness, color } or null.
 */
function readRule(inner, sheet) {
  if (visibleText(inner) !== '' || /<img\b/i.test(inner)) return null;
  const nodes = significant(topLevelNodes(inner)).filter(node => node.kind === 'element');
  if (nodes.length === 0) return null;
  const found = nodes
    .map((node) => {
      const decl = resolved(node, sheet);
      if (node.tag === 'hr') {
        // `hr { background: black; height: 1px }` is how the hand-built family
        // draws its rule. Both are measurements; taking the block's pale grey
        // default over them would repaint a black line the colour of a hairline.
        const height = px(decl.height);
        return {
          thickness: height === null || height < 1 ? 1 : height,
          color: fillColour(decl) || BLOCK_DEFAULTS.divider.color,
        };
      }
      const border = decl['border-top'];
      if (!border) return null;
      return {
        thickness: px(border) === null ? 1 : px(border),
        color: borderColour(border) || BLOCK_DEFAULTS.divider.color,
      };
    })
    .filter(Boolean);
  return found.length === 1 ? found[0] : null;
}

// --- copy that is not in a cell at all --------------------------------------

// The children a table and a row are allowed to have. Anything else written
// directly inside one is FOSTER-PARENTED by every HTML parser: the browser lifts
// it out of the table and renders it just before, which is why an email built
// this way looks perfectly correct today.
// `table` is in the list because a <table> written inside a <table> is not
// foster-parented — the parser ends the outer one and starts a sibling — and
// either way it is structure the ordinary cell walk reaches on its own.
const TABLE_CHILDREN = ['table', 'tr', 'thead', 'tbody', 'tfoot', 'caption', 'colgroup', 'col', 'td', 'th'];
const ROW_CHILDREN = ['td', 'th'];

// The elements that establish a foster context of their own. Descend into one
// of these ALWAYS, whatever it holds: a <tr> whose only child is an <h2> holds
// no table markup at all, and that <h2> is the whole point.
const FOSTER_PARENTS = ['table', 'tbody', 'thead', 'tfoot', 'tr'];

// Elsewhere, only descend where a table could be. These documents are 10-14KB
// and the walk is a regex scan per element; without this it is a scan per
// element of the whole document.
const HOLDS_TABLE_MARKUP = /<(table|tbody|thead|tfoot|tr|td|th)\b/i;

/**
 * Every run of content written directly inside a `<tr>` or a `<table>`, as
 * { start, end, tagStart, tagHtml } over the run's own markup.
 *
 * WHY THESE ARE CONTENT AND NOT A MISTAKE TO REFUSE ON. The oldest templates in
 * this corpus write their entire message this way:
 *
 *   <tr class="h2" style="text-align: center;">
 *     <h2>You are one step away from confirming your subscription…</h2>
 *   </tr>
 *   <tr class="bodycopy" align="center">
 *     <a class="button" href="{{{confirmationLink}}}">Accept</a>
 *   </tr>
 *
 * There is no `<td>` anywhere in that. It is invalid HTML, and it is also what
 * every email client renders — the parser foster-parents both elements out of
 * the table and draws them, which is why these emails have looked right for
 * years. The converter counted them as "copy outside every table cell … it
 * would simply be missing from the converted email", and that came to 91-100%
 * of these emails' words: a refusal of the whole template on the grounds that
 * the whole template exists.
 *
 * So a run of foster children is treated exactly as a cell's contents are — the
 * same `readImages`, the same `readRule`, the same `classifyCell`. What it is
 * NOT given is a free pass: a run that nests a table or a cell is dropped here
 * (the ordinary cell walk reaches those cells on its own), a run holding no
 * words and no picture is chrome, and a run that reads as nothing is reported
 * unplaced with everything else.
 *
 * Contiguous runs, because contiguity is what the browser preserves: a `<tr>`
 * holding `<td>a</td><br><td>b</td>` foster-parents only the `<br>`, and the two
 * cells stay where they are.
 *
 * One scanner. `topLevelNodes` — this module's only reader, and comment-aware
 * through `scanOutsideComments` — carries the offsets that make the recursion
 * possible; nothing new parses HTML here.
 */
function fosterRuns(html, from, sheet) {
  const runs = [];

  const visit = (fragment, base, parent) => {
    const allowed = (parent.tag === 'tr' && ROW_CHILDREN)
      || (FOSTER_PARENTS.indexOf(parent.tag) !== -1 && TABLE_CHILDREN)
      || null;
    const nodes = topLevelNodes(fragment);
    let run = null;

    const flush = () => {
      if (run) runs.push(run);
      run = null;
    };
    const extend = (start, end) => {
      if (run === null) {
        run = { start: base + start, end: base + end, tagStart: parent.start, tagHtml: parent.html };
      } else {
        run.end = base + end;
      }
    };

    nodes.forEach((node) => {
      if (node.kind === 'text') {
        // Whitespace between two children is formatting; it neither starts a run
        // nor breaks one. Words written straight into a <tr> are content.
        if (allowed && visibleText(node.raw) !== '') extend(node.outerStart, node.outerEnd);
        return;
      }
      if (allowed && allowed.indexOf(node.tag) === -1) {
        extend(node.outerStart, node.outerEnd);
      } else {
        flush();
      }
      if (FOSTER_PARENTS.indexOf(node.tag) !== -1 || HOLDS_TABLE_MARKUP.test(node.inner)) {
        visit(node.inner, base + node.innerStart, {
          tag: node.tag,
          start: base + node.outerStart,
          html: `<${node.tag}${node.attrs}>`,
        });
      }
    });
    flush();
  };

  visit(html.slice(from), from, { tag: '', start: -1, html: '' });

  return runs
    .map(run => ({ ...run, inner: html.slice(run.start, run.end) }))
    // A run that nests a table or a cell is layout, and the cells inside it are
    // reached by the ordinary walk. A run that holds no words, no picture and no
    // rule — the stray <br> between two <td>s — is chrome, and chrome is meant
    // to be dropped. The `<tr><hr /></tr>` that draws the line above the social
    // row has none of the first two and is content all the same.
    .filter(run => isLeaf(run.inner))
    .filter(run => visibleText(run.inner) !== ''
      || /<img\b/i.test(run.inner)
      || readRule(run.inner, sheet) !== null)
    .sort((a, b) => a.start - b.start);
}

/**
 * The document's visible words that lie outside every one of `spans`.
 *
 * The same cut-out-and-measure-the-remainder shape `cellCoverage` uses, and for
 * the same reason: comparing a sum of cell lengths against the whole document
 * charges the answer for every space between two adjacent cells, which no
 * converter could ever recover. The spans must be in document order and must not
 * overlap — leaf cells nest no other cell, so they satisfy both.
 *
 * `<head>`, `<style>` and `<script>` go first. Their contents are not tags, so
 * visibleText would count every media query as document copy, and these emails
 * carry kilobytes of it.
 */
function textOutside(html, spans) {
  let cursor = 0;
  const parts = [];
  spans.forEach((span) => {
    parts.push(html.slice(cursor, span.start));
    cursor = span.end;
  });
  parts.push(html.slice(cursor));
  return visibleText(parts
    .join(' ')
    .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' '));
}

// --- document settings ------------------------------------------------------

const bodyOf = (html) => {
  const at = html.search(/<body\b/i);
  return at === -1 ? html : html.slice(at);
};

// Narrower than this and an element is something inside the card, not the card.
const CARD_MIN_WIDTH = 320;

/**
 * The card: the first element in the body that fixes its own width in pixels.
 *
 * WHY THIS IS NOT `/max-width:(\d+)px/` ANY MORE. It used to be, and on MJML it
 * was right by luck: MJML's first pixel width in the body is the wrapper
 * `<div style="margin:0px auto;max-width:600px;">`. Braze's first one is the
 * MASTHEAD — `<img style="max-width:390px;width:100%">` — so every Braze
 * template converted to a 390px card, which then clamped its own 648px hero down
 * to 390 as well. A whole family of emails would have shipped at 60% of their
 * width, and the side-by-side would have shown it as "narrower", not as "wrong".
 *
 * So: skip `<img>` (a picture's width is the picture's, never the card's), skip
 * anything under 320px (that is something inside the card), and take the first
 * element left. On MJML that is still the wrapper div and the answer is
 * unchanged; on Braze it is `<table class="row-content" style="…;width:650px;
 * margin:0 auto">`, which is the card by name as well as by measurement.
 *
 * Its background comes back with it. The page and the card are different
 * colours in this corpus — a white page around a warm grey card — and painting
 * the card the page's colour erases the card.
 *
 * The width and the background are read through the stylesheet as well as the
 * tag, because the hand-built family writes neither on the tag: its card is
 * `<table bgcolor="#ffffff" class="content">` over `.content { width:100%;
 * max-width:900px }`. A `bgcolor` attribute is as explicit a declaration as a
 * `background-color`, and it is the only one those templates give.
 */
function cardFrom(body, sheet) {
  const found = scanOutsideComments(body, ANY_TAG)
    .filter(match => match[1] !== '/' && match[2].toLowerCase() !== 'img')
    .map((match) => {
      const node = { tag: match[2].toLowerCase(), attrs: match[3] };
      const decl = resolved(node, sheet);
      const maxWidth = px(decl['max-width']);
      const width = maxWidth === null ? px(decl.width) : maxWidth;
      return width === null ? null : {
        width,
        background: fillColour(decl) || attrOf(node, 'bgcolor').trim(),
      };
    })
    .filter(entry => entry !== null && entry.width >= CARD_MIN_WIDTH);
  return found.length ? found[0] : null;
}

/**
 * The card's own width, background and the type its copy is set in.
 *
 * The background is only ever read from an explicit declaration — an email with
 * no `bgcolor` on its body is white, and the block model's own default
 * (#efeae7, a warm grey) would repaint every converted email a colour it never
 * had.
 */
function settingsFrom(html, textBlocks, sheet) {
  const body = bodyOf(html);
  const bodyTag = /<body\b[^>]*>/i.exec(body);
  const bodyAttrs = bodyTag ? { attrs: bodyTag[0] } : { attrs: '' };
  const bodyStyle = declarations(attrOf(bodyAttrs, 'style'));
  const background = attrOf(bodyAttrs, 'bgcolor')
    || (bodyStyle['background-color'] || '').trim()
    || '#ffffff';

  const card = cardFrom(body, sheet);
  const width = card ? clamp(card.width, 320, 900, DEFAULT_SETTINGS.width) : DEFAULT_SETTINGS.width;

  // The document's body type is whatever most of its paragraphs are set in.
  // Modal rather than first: one differently-styled aside must not restyle the
  // whole email, and the props that agree with the document are then left off
  // their blocks, so changing the email's font in the inspector still works.
  const modal = (key, fallback) => {
    const counts = {};
    textBlocks.forEach((block) => {
      const value = block[key];
      if (value === '' || value === undefined || value === null) return;
      counts[value] = (counts[value] || 0) + 1;
    });
    const keys = Object.keys(counts);
    if (keys.length === 0) return fallback;
    return keys.reduce((best, key2) => (counts[key2] > counts[best] ? key2 : best), keys[0]);
  };

  const size = Number(modal('fontSize', DEFAULT_SETTINGS.fontSize));
  const height = Number(modal('lineHeight', DEFAULT_SETTINGS.lineHeight));

  return {
    width,
    background,
    // The card's own colour when it declares one — the Braze family paints a
    // warm grey card on a white page — and the page's colour when it does not,
    // which is the MJML family, where the two really are the same and claiming
    // otherwise would be an invention.
    cardBackground: (card && card.background) || background,
    fontFamily: String(modal('fontFamily', DEFAULT_SETTINGS.fontFamily)),
    fontSize: Number.isFinite(size) ? clamp(size, 8, 96, DEFAULT_SETTINGS.fontSize) : DEFAULT_SETTINGS.fontSize,
    lineHeight: Number.isFinite(height) ? clamp(height, 8, 200, DEFAULT_SETTINGS.lineHeight) : DEFAULT_SETTINGS.lineHeight,
    textColor: String(modal('color', DEFAULT_SETTINGS.textColor)),
    linkColor: DEFAULT_SETTINGS.linkColor,
  };
}

// --- what changes even where it converts ------------------------------------

const countMatches = (html, pattern) => (html.match(pattern) || []).length;

function changesIn(html, notes) {
  const changes = [];
  const conditionals = countMatches(html, /<!--\[if/gi);
  if (conditionals > 0) {
    changes.push(`${conditionals} Outlook conditional comment${conditionals === 1 ? '' : 's'} are not carried across. The blocks generate their own, which are not the same ones.`);
  }
  if (/<v:|urn:schemas-microsoft-com:vml/i.test(html)) {
    changes.push('The VML fallback markup is dropped. Outlook falls back to the block serialiser’s own tables instead.');
  }
  const media = countMatches(html, /@media/gi);
  if (media > 0) {
    changes.push(`${media} @media rule${media === 1 ? '' : 's'} are dropped. The blocks are responsive by being a single column, not by restyling at a breakpoint.`);
  }
  if (/@font-face/i.test(html)) {
    changes.push('The @font-face declarations are dropped; the blocks use the web-safe stack in the document settings.');
  }
  const rules = readStyleSheet(html).length;
  if (rules > 0) {
    changes.push(`The <style> block itself is not carried across — the serialised email has no stylesheet, only inline styles. Its ${rules} rule${rules === 1 ? '' : 's'} were READ (that is where the image widths, the card width and the button’s colours came from), but `
      + 'any of them the block model has no prop for — a bold weight, a letter-spacing, a padding on a class — reverts to the document’s own type.');
  }
  changes.push('The table nesting and every cell’s padding are regenerated, not preserved. Spacing will shift by a few pixels throughout.');
  changes.push('Links inside the copy take the document’s link colour, which the original left to the client.');
  notes.forEach((note) => { if (changes.indexOf(note) === -1) changes.push(note); });
  return changes;
}

// --- the converter ----------------------------------------------------------

let sequence = 0;
// Ids only have to be unique within one document and stable for one run. They
// are NOT emailBlocks' `createBlock` ids on purpose: a conversion the user
// abandons must not consume ids from the same counter the canvas is using, and a
// converted document has to serialise identically for the same input so the
// side-by-side comparison does not churn.
const convertedId = (prefix) => {
  sequence += 1;
  return `conv-${prefix}-${sequence}`;
};

/**
 * Convert one email document into blocks.
 *
 * Returns:
 *   ok           true only when NOTHING was left unplaced.
 *   reason       '' when ok; a sentence naming the first obstacle otherwise.
 *   doc          the block document, or null when there is nothing to show.
 *   unconverted  [{ what, where, reason }] — content this could not place.
 *   changes      [string] — what differs even where it did convert.
 *   stats        counts the caller can show without recomputing them.
 */
function htmlToBlocks(html) {
  // Reset per call: the same input must produce the same document, or the
  // side-by-side comparison churns for no reason and the dirty check lies.
  sequence = 0;
  const unconverted = [];
  const notes = [];
  const blocks = [];

  if (!html || typeof html !== 'string') {
    return {
      ok: false, reason: 'There is no HTML to convert.', doc: null, unconverted, changes: [], stats: null,
    };
  }

  if (/background-image\s*:/i.test(html)) {
    unconverted.push({
      what: 'a background image',
      where: 'the document',
      reason: 'No block type can carry a background image, and an email built on one loses its whole design without it.',
    });
  }

  const body = bodyOf(html);
  const bodyOffset = html.length - body.length;
  // The document's own base style rules. The oldest templates keep every
  // measurement here — image widths, the card width, the button's chip — and
  // wrote nothing on the tag. See ./emailStyleSheet.
  const sheet = readStyleSheet(html);
  const editable = {};
  findEditableCells(html).forEach((cell) => { editable[cell.start] = cell; });

  // Every leaf cell in document order — copy, images and rules alike. One walk,
  // one scanner; see this file's header for why there is not a second.
  const cells = cellSpans(html)
    .filter(span => span.start >= bodyOffset)
    .map(span => ({
      start: span.start,
      end: span.end,
      inner: html.slice(span.start, span.end),
      tagStart: 0,
      tagHtml: '',
      foster: false,
    }))
    .filter(entry => isLeaf(entry.inner))
    .map((entry) => {
      const tagHtml = openingTag(html, entry.start);
      return { ...entry, tagHtml, tagStart: entry.start - tagHtml.length };
    });

  // …and every run of content written directly inside a <tr> or a <table>, which
  // every client foster-parents out and draws. See `fosterRuns`: without these,
  // 91-100% of the oldest templates' words are "outside every table cell".
  //
  // A foster run can neither contain a leaf cell nor be contained by one — it is
  // dropped unless it is a leaf, and a leaf holds no cell — so merging the two
  // lists by offset gives spans in document order that do not overlap, which is
  // what `textOutside` needs of them.
  const fosters = fosterRuns(html, bodyOffset, sheet).map(run => ({ ...run, foster: true }));
  const leaves = cells.concat(fosters).sort((a, b) => a.start - b.start);

  // Pass one: each leaf becomes at most one entry, classified on its own.
  const entries = [];
  leaves.forEach((span) => {
    const { inner, foster } = span;
    const cellTag = span.tagHtml;
    const cell = foster ? null : editable[span.start];

    if (cell) {
      const verdict = classifyCell(inner, sheet);
      verdict.notes.forEach((note) => { if (notes.indexOf(note) === -1) notes.push(note); });
      if (verdict.type === 'unmapped') {
        unconverted.push({
          what: `the copy “${visibleText(inner).slice(0, 60)}”`,
          where: cell.id,
          reason: verdict.reason,
        });
        return;
      }
      entries.push({ kind: verdict.type, props: verdict.props, where: cell.id });
      return;
    }

    const images = readImages(inner, cellTag, sheet);
    if (images) {
      if (images.some(image => !image.src)) {
        unconverted.push({ what: 'an image', where: `offset ${span.start}`, reason: 'the <img> has no src' });
        return;
      }
      const chip = chipAround(html, span.tagStart, sheet);

      // THE FULL-BLEED HERO. `<img class="image-header">` over `img { width:100% }`
      // and no `.image-header` rule at all: the document says, in its own words,
      // that this picture fills whatever holds it, and what holds it is a card
      // whose width the document also states. That is a derivation from two
      // measurements, not a guess — and it is said out loud in `changes`, because
      // the number came from the card rather than from the image. Only ever for a
      // picture alone in its cell: a 100%-wide icon in a row of six is not a
      // reading anyone could defend.
      const container = images.length === 1 && images[0].fills && !images[0].width
        ? containerWidth(html, span.tagStart, sheet)
        : null;
      const sized = images.map((image) => {
        if (image.width || container === null) return image;
        return { ...image, width: container, derived: true };
      });
      if (sized.some(image => image.derived)) {
        notes.push(`a full-bleed image carries no width of its own — only \`width:100%\` — so it is placed at ${container}px, the width of the box the document puts it in`);
      }

      const unsized = sized.find(image => !image.width);
      if (unsized) {
        unconverted.push({
          what: `the image ${unsized.src.slice(0, 60)}`,
          where: `offset ${span.start}`,
          reason: 'it has no measurable width — not on the tag, not in its style, not in the document’s stylesheet, and a guessed width would be visibly wrong',
        });
        return;
      }

      if (sized.length === 1) {
        entries.push({
          kind: 'img',
          props: { ...sized[0], chip },
          where: `offset ${span.start}`,
        });
        return;
      }

      // Several pictures and nothing else in one cell: a row, by the document's
      // own layout. Widths are per icon, so the 100px Radio Primavera Sound logo
      // beside five 60px glyphs stays 100px.
      entries.push({
        kind: 'row',
        props: { icons: sized.map(image => ({ ...image, chip })) },
        where: `offset ${span.start}`,
      });
      return;
    }

    const rule = readRule(inner, sheet);
    if (rule) {
      const padding = allPx(resolved(nodeFromTag(cellTag), sheet).padding);
      entries.push({
        kind: 'divider',
        props: {
          color: rule.color,
          thickness: rule.thickness,
          spacing: padding.length ? padding[0] : BLOCK_DEFAULTS.divider.spacing,
        },
        where: `offset ${span.start}`,
      });
      return;
    }

    // A leaf the copy finder withheld is nearly always withheld for the MSO
    // conditional in it, and in this corpus there is exactly one shape that
    // reliably is: a button. Braze and MJML both write the Outlook
    // `<v:roundrect>` fallback as a SIBLING of the anchor, inside the same cell,
    // so `findEditableCells` — which refuses any cell holding a conditional,
    // rightly, because it hands cells to an editor — never offers it.
    //
    // ONLY a button verdict is taken here. classifyCell reads through a
    // conditional for the button and for nothing else, so prose or an image
    // cannot come in by this door. The fragment is checked balanced first: the
    // node reader's guarantee is that it is only ever pointed at balanced
    // markup, and this is the one call site that reaches cells the finder has
    // not already proved.
    //
    // A FOSTER RUN IS THE EXCEPTION, and for a reason that is about why the
    // finder withheld it rather than about what is in it. `findEditableCells`
    // never offered this content because it is not in a cell at all — there is
    // no <td> for it to be the contents of — not because it holds a second
    // Outlook rendering. So the full verdict is taken, exactly as it would be
    // for the same markup one <td> deeper. Every refusal `classifyCell` makes
    // still applies: a comment in it, an image among the copy, a tag no block
    // carries.
    // A fragment that only fails `isTagBalanced` for a closing tag with no
    // opener is read from the repaired copy — see `dropStrayCloses`, which
    // refuses anything more ambitious than that. The finder withheld the cell
    // for the imbalance and for nothing else, so the full verdict is taken,
    // exactly as it would be for the same copy without the stray tag.
    const balanced = isTagBalanced(inner);
    const repaired = balanced ? null : dropStrayCloses(inner);
    if (repaired) {
      notes.push(`a stray </${repaired.dropped.join('>, </')}> that closes nothing was dropped from one cell — every word around it is kept, and the block model has no way to carry a tag with no opener`);
    }
    let readable = null;
    if (balanced) readable = inner;
    else if (repaired) readable = repaired.html;
    const withheld = readable === null ? null : classifyCell(readable, sheet);
    if ((foster || repaired) && withheld && withheld.type !== 'unmapped') {
      withheld.notes.forEach((note) => { if (notes.indexOf(note) === -1) notes.push(note); });
      entries.push({
        kind: withheld.type,
        props: withheld.props,
        where: `foster-parented at offset ${span.start}`,
      });
      return;
    }
    if (withheld && withheld.type === 'button') {
      withheld.notes.forEach((note) => { if (notes.indexOf(note) === -1) notes.push(note); });
      entries.push({ kind: 'button', props: withheld.props, where: `offset ${span.start}` });
      return;
    }

    // A cell with an image among other things, or a comment, or markup this does
    // not read: content, and unplaced. A cell with nothing in it at all is
    // chrome — a spacer, a shim — and is meant to be dropped.
    //
    // The refusal names what is actually in the cell. "Cannot read as a block"
    // on its own sent a reader to a byte offset to find out that all three cells
    // named were the same ordinary wrapper; the inventory is the difference
    // between a report and a scavenger hunt.
    if (/<img\b/i.test(inner) || visibleText(inner) !== '') {
      const inventory = [];
      const pictures = scanOutsideComments(inner, '<img\\b').length;
      if (pictures > 0) inventory.push(`${pictures} <img>`);
      if (/<!--\[if/i.test(inner)) inventory.push('an Outlook conditional');
      if (!isTagBalanced(inner)) inventory.push('markup that is not tag-balanced on its own');
      const copy = visibleText(inner);
      if (copy !== '') inventory.push(`the copy “${copy.slice(0, 60)}”`);
      if (withheld && withheld.reason) inventory.push(withheld.reason);
      unconverted.push({
        what: `the cell at offset ${span.start}`,
        where: `offset ${span.start}`,
        reason: `it holds content this converter cannot read as a block — ${inventory.join('; ')}`,
      });
    }
  });

  // Pass two: a run of adjacent small images is ONE social row.
  //
  // "Adjacent" is adjacency in this list, not in the bytes: two icons separated
  // by a kilobyte of MJML ghost tables are still siblings, and two separated by a
  // paragraph or by a masthead are not. That is exactly the rule a reader would
  // use looking at the rendered email.
  const socialBlock = (icons) => {
    if (icons.some(icon => !icon.chip.background)) {
      notes.push('one or more social icons had no coloured chip to recover — if the glyphs are white they will be invisible on a light card, and each icon has its own chip colour in Block properties');
    }
    return {
      id: convertedId('soc'),
      type: 'social',
      align: 'center',
      gap: 8,
      icons: icons.map(icon => ({
        src: icon.src,
        href: icon.href,
        alt: icon.alt,
        width: icon.width,
        background: icon.chip.background,
        radius: icon.chip.radius,
      })),
    };
  };

  const imageBlock = props => ({
    id: convertedId('img'),
    type: 'image',
    src: props.src,
    alt: props.alt,
    href: props.href,
    width: props.width,
    align: 'center',
  });

  let index = 0;
  while (index < entries.length) {
    const entry = entries[index];
    if (entry.kind === 'row') {
      // A cell that was already a row of pictures in the document. No grouping
      // rule had to decide it: the markup did.
      blocks.push(socialBlock(entry.props.icons));
      index += 1;
      // eslint-disable-next-line no-continue
      continue;
    }

    if (entry.kind !== 'img') {
      blocks.push({ id: convertedId(entry.kind.slice(0, 3)), type: entry.kind, ...entry.props });
      index += 1;
      // eslint-disable-next-line no-continue
      continue;
    }

    if (entry.props.width >= IMAGE_MIN_WIDTH) {
      blocks.push(imageBlock(entry.props));
      index += 1;
      // eslint-disable-next-line no-continue
      continue;
    }

    if (entry.props.width > ICON_MAX_WIDTH) {
      // Between the two sizes. The size itself is not in doubt — it was
      // measured — so the only open question is whether this belongs in a ROW
      // with what sits next to it, and that question only exists when what sits
      // next to it is icon-sized. A 140px app-store badge between a paragraph
      // and a 179px badge has no icon beside it to join, so it is a picture, and
      // it is placed at the width the document gave it.
      const ambiguous = [entries[index - 1], entries[index + 1]].some(neighbour => (
        neighbour && neighbour.kind === 'img' && neighbour.props.width <= ICON_MAX_WIDTH
      ));
      if (ambiguous) {
        unconverted.push({
          what: `the image ${entry.props.src.slice(0, 60)}`,
          where: entry.where,
          reason: `it is ${entry.props.width}px, sitting next to icons — too small to be a picture of its own and too big to be one of the row, so this converter will not guess which it is`,
        });
        index += 1;
        // eslint-disable-next-line no-continue
        continue;
      }
      const beside = [entries[index - 1], entries[index + 1]].some(neighbour => (
        neighbour && (neighbour.kind === 'img' || neighbour.kind === 'row')
      ));
      if (beside) {
        notes.push('two pictures that shared one table row are stacked one above the other — the block document has no side-by-side row for pictures, only for icons');
      }
      blocks.push(imageBlock(entry.props));
      index += 1;
      // eslint-disable-next-line no-continue
      continue;
    }

    const run = [];
    while (index < entries.length
      && entries[index].kind === 'img'
      && entries[index].props.width <= ICON_MAX_WIDTH) {
      run.push(entries[index].props);
      index += 1;
    }
    blocks.push(socialBlock(run));
  }

  // Copy that is in no cell at all is copy this cannot place.
  //
  // MEASURED AGAINST THE LEAVES, NOT AGAINST `cellCoverage`. cellCoverage counts
  // the cells `findEditableCells` returns, which is deliberately a subset: it
  // withholds the picture cells, the rule cells and — the case that mattered —
  // any cell holding an Outlook conditional. So a CONFIRM button read perfectly
  // well by the walk above was ALSO reported here as "1% of this email's words
  // outside every table cell", and a reader chasing two independent-looking
  // refusals found one cell. This walk visits every leaf, and every leaf with
  // copy in it either became a block or got its own entry above, so what is left
  // over here is exactly the copy nothing accounted for — no double count.
  //
  // `visibleText` is still the finder's own, so "what counts as text" cannot
  // drift between the two measurements. `cellCoverage` stays in `stats`, where
  // it answers the question it was written for: how much of this email the CELL
  // EDITOR can reach.
  const outside = textOutside(html, leaves.map(entry => ({ start: entry.start, end: entry.end })));
  if (outside !== '') {
    const total = textOutside(html, []).length || 1;
    unconverted.push({
      what: `${Math.max(1, Math.round((outside.length / total) * 100))}% of this email’s words: “${outside.slice(0, 140)}${outside.length > 140 ? '…' : ''}”`,
      where: 'outside every table cell',
      reason: 'the copy is written directly under a <table> or <tr>, or in a cell that also nests a table, where no block can hold it — it would simply be missing from the converted email',
    });
  }

  // Nothing may go missing quietly. Every image the document has, outside its
  // comments, must be in a block.
  const imagesInDocument = scanOutsideComments(body, '<img\\b').length;
  const imagesPlaced = blocks.reduce((sum, block) => {
    if (block.type === 'image') return sum + 1;
    if (block.type === 'social') return sum + block.icons.length;
    return sum;
  }, 0);
  if (imagesPlaced < imagesInDocument) {
    unconverted.push({
      what: `${imagesInDocument - imagesPlaced} image${imagesInDocument - imagesPlaced === 1 ? '' : 's'}`,
      where: 'the document',
      reason: 'they are not in a cell of their own — wrapped in a layout <div>, sized by a CSS class, or sharing a cell with something else — so a converted email would simply not have them',
    });
  }

  if (blocks.length === 0) {
    unconverted.push({
      what: 'the whole email',
      where: 'the document',
      reason: 'nothing in it reads as a block',
    });
  }

  const textBlocks = blocks.filter(block => block.type === 'text');
  const settings = settingsFrom(html, textBlocks, sheet);

  // A typography prop that agrees with the document is left off its block, so
  // the document settings still govern it and changing the email's font in the
  // inspector still does something. Only the outliers say anything.
  const inherit = (block) => {
    const next = { ...block };
    [['fontSize', 'fontSize'], ['lineHeight', 'lineHeight'], ['color', 'textColor'], ['fontFamily', 'fontFamily']]
      .forEach(([prop, setting]) => {
        if (next[prop] !== undefined && String(next[prop]) === String(settings[setting])) next[prop] = '';
      });
    return next;
  };

  const doc = {
    version: 1,
    settings,
    blocks: blocks.map(block => (block.type === 'text' ? inherit(block) : block)),
  };

  const ok = unconverted.length === 0;
  return {
    ok,
    reason: ok ? '' : `${unconverted.length} thing${unconverted.length === 1 ? '' : 's'} in this email cannot be converted without losing them.`,
    // The document is returned even on a refusal: a reviewer deciding whether to
    // accept a partial conversion needs to see what it WOULD be, and the UI
    // refuses to save it regardless of what it shows.
    doc,
    unconverted,
    changes: changesIn(html, notes),
    stats: {
      // The cell editor's reach, not this converter's — see the note by
      // `textOutside` above for why they are two different numbers.
      coverage: cellCoverage(html),
      images: imagesInDocument,
      blocks: doc.blocks.length,
      byType: doc.blocks.reduce((counts, block) => ({
        ...counts, [block.type]: (counts[block.type] || 0) + 1,
      }), {}),
    },
  };
}

module.exports = {
  htmlToBlocks,
  fosterRuns,
  dropStrayCloses,
  // The inverse mapping, exported so blockConversionProbe.js measures with the
  // same rules the converter converts with rather than its own copy.
  classifyCell,
  // The node reader, for the same reason.
  topLevelNodes,
  // Tested directly: it is the one thing here that CHANGES the copy rather than
  // classifying it, so its identity on already-blocked copy is a property worth
  // asserting on its own and not only through a whole conversion.
  paragraphWrapLoose,
  significant,
  styleOf,
  attrOf,
  tagsIn,
  countTag,
  declarations,
  TEXT_TAGS,
  HEADING_INLINE,
  WRAPPER_TAGS,
  IMAGE_MIN_WIDTH,
  ICON_MAX_WIDTH,
};
